import * as React from "react"
import { Info, X } from "lucide-react"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { LoadingSpinner } from "@/components/shared/loading-spinner"
import {
  getAlfredMaxLengthError,
  MAX_ALFRED_INDEXED_STRING_LENGTH,
} from "@/lib/alfred-field-validation"
import {
  AlfredDeviceFormFields,
  DEFAULT_CLONE_PROTECTED_FIELD_MODES,
  EMPTY_FORM_DATA,
  formDataToClone,
  formDataToCreate,
  type CloneProtectedFieldModes,
  type AlfredDeviceFormData,
} from "@/components/alfred/alfred-device-form-fields"
import type { AlfredClientConfig } from "@/types/alfred-client"
import type {
  CloneAlfredDeviceRequest,
  CloneField,
  CreateAlfredDeviceRequest,
} from "@/types/alfred-device"

interface AddAlfredDeviceFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: CreateAlfredDeviceRequest) => Promise<void>
  onCloneSubmit: (sourceDevicePk: number, data: CloneAlfredDeviceRequest) => Promise<void>
  isSubmitting?: boolean
  clients: AlfredClientConfig[]
  defaultClientId?: string
  // When provided, the form opens pre-filled (clone). deviceId should already
  // be cleared by the caller so the user must enter a unique one.
  initialData?: AlfredDeviceFormData | null
  cloneSourceDevicePk?: number | null
}

type FormErrors = Partial<Record<keyof AlfredDeviceFormData, string>>
type RequiredIdentityField = "deviceId" | "clientId" | "sotiDeviceId"

function validateCollectionSecrets(
  data: AlfredDeviceFormData,
  protectedFieldModes?: CloneProtectedFieldModes,
): FormErrors {
  let proVenueInstances: string | undefined
  let appleVendors: string | undefined
  let googleVendors: string | undefined
  const proVenueMode = protectedFieldModes?.proVenueInstances
  if (proVenueMode === "replace" && data.proVenueInstances.length === 0) {
    proVenueInstances = "Add at least one ProVenue instance or choose another value source."
  } else if (
    proVenueMode !== "clone" &&
    proVenueMode !== "global" &&
    data.proVenueInstances.some(
      (item) => !item.consumerKey.trim() || !item.consumerSecret.trim(),
    )
  ) {
    proVenueInstances = "Enter a consumer key and consumer secret for every ProVenue instance."
  }

  const appleMode = protectedFieldModes?.appleVendors
  if (appleMode === "replace" && data.appleVendors.length === 0) {
    appleVendors = "Add at least one Apple vendor credential or use global defaults."
  } else if (
    appleMode !== "clone" &&
    appleMode !== "global" &&
    data.appleVendors.some((item) => getAlfredMaxLengthError("Vendor", item.vendor))
  ) {
    appleVendors = `Apple vendor names cannot be longer than ${MAX_ALFRED_INDEXED_STRING_LENGTH} characters.`
  } else if (
    appleMode !== "clone" &&
    appleMode !== "global" &&
    data.appleVendors.some((item) => !item.password?.trim())
  ) {
    appleVendors = "Enter a password for every Apple vendor credential."
  }

  const googleMode = protectedFieldModes?.googleVendors
  if (googleMode === "replace" && data.googleVendors.length === 0) {
    googleVendors = "Add at least one Google vendor credential or use global defaults."
  } else if (
    googleMode !== "clone" &&
    googleMode !== "global" &&
    data.googleVendors.some((item) => getAlfredMaxLengthError("Vendor", item.vendor))
  ) {
    googleVendors = `Google vendor names cannot be longer than ${MAX_ALFRED_INDEXED_STRING_LENGTH} characters.`
  } else if (
    googleMode !== "clone" &&
    googleMode !== "global" &&
    data.googleVendors.some((item) => !item.password?.trim())
  ) {
    googleVendors = "Enter a password for every Google vendor credential."
  }

  return {
    appleVendors,
    googleVendors,
    proVenueInstances,
  }
}

function validateCloneReplacement(
  field: CloneField,
  data: AlfredDeviceFormData,
  modes: CloneProtectedFieldModes,
): string | undefined {
  if (modes[field] !== "replace") return undefined
  if (field === "configurationPassword" && !data.configurationPassword.trim()) {
    return "Enter a replacement configuration password."
  }
  if (field === "ticketmasterConsumerKey" && !data.ticketmasterConsumerKey.trim()) {
    return "Enter a replacement Ticketmaster consumer key."
  }
  return undefined
}

export function AddAlfredDeviceForm({
  open,
  onOpenChange,
  onSubmit,
  onCloneSubmit,
  isSubmitting = false,
  clients,
  defaultClientId,
  initialData,
  cloneSourceDevicePk,
}: AddAlfredDeviceFormProps) {
  const isClone = initialData != null && cloneSourceDevicePk != null
  const [data, setData] = React.useState<AlfredDeviceFormData>(EMPTY_FORM_DATA)
  const [cloneProtectedFieldModes, setCloneProtectedFieldModes] =
    React.useState<CloneProtectedFieldModes>(DEFAULT_CLONE_PROTECTED_FIELD_MODES)
  const [errors, setErrors] = React.useState<FormErrors>({})
  const [touched, setTouched] = React.useState<Partial<Record<keyof AlfredDeviceFormData, boolean>>>({})

  React.useEffect(() => {
    if (open) {
      setData(initialData ?? { ...EMPTY_FORM_DATA, clientId: defaultClientId ?? "" })
      setCloneProtectedFieldModes({ ...DEFAULT_CLONE_PROTECTED_FIELD_MODES })
      setErrors({})
      setTouched({})
    }
  }, [open, defaultClientId, initialData])

  const validate = (field: RequiredIdentityField, value: string): string | undefined => {
    if (!value.trim()) {
      if (field === "deviceId") return "Device ID is required"
      if (field === "sotiDeviceId") return "SOTI Device ID is required"
      return "Client is required"
    }
    if (field === "deviceId") {
      const lengthError = getAlfredMaxLengthError("Device ID", value)
      if (lengthError) return lengthError
    }
    if (field === "deviceId" && !/^[A-Za-z0-9-_]+$/.test(value)) {
      return "Device ID can only contain letters, numbers, hyphens, and underscores"
    }
    if (
      field === "clientId" &&
      !clients.some((client) => client.clientId === value && Number.isInteger(client.id) && client.id > 0)
    ) {
      return "Select a valid client"
    }
    return undefined
  }

  const handleChange = (field: keyof AlfredDeviceFormData, value: string | boolean | null | unknown[]) => {
    setData((prev) => ({ ...prev, [field]: value }))
    if ((field === "deviceId" || field === "clientId" || field === "sotiDeviceId") && touched[field]) {
      setErrors((prev) => ({ ...prev, [field]: validate(field, value as string) }))
    }
    if (
      field === "appleVendors" ||
      field === "googleVendors" ||
      field === "proVenueInstances"
    ) {
      const nextData = { ...data, [field]: value }
      setErrors((prev) => ({
        ...prev,
        [field]: validateCollectionSecrets(
          nextData,
          isClone ? cloneProtectedFieldModes : undefined,
        )[field],
      }))
    }
    if (
      isClone &&
      (field === "configurationPassword" || field === "ticketmasterConsumerKey") &&
      touched[field]
    ) {
      const nextData = { ...data, [field]: value }
      setErrors((prev) => ({
        ...prev,
        [field]: validateCloneReplacement(field, nextData, cloneProtectedFieldModes),
      }))
    }
  }

  const handleBlur = (field: keyof AlfredDeviceFormData) => {
    setTouched((prev) => ({ ...prev, [field]: true }))
    if (field === "deviceId" || field === "clientId" || field === "sotiDeviceId") {
      setErrors((prev) => ({ ...prev, [field]: validate(field, data[field] as string) }))
    }
    if (
      isClone &&
      (field === "configurationPassword" || field === "ticketmasterConsumerKey")
    ) {
      setErrors((prev) => ({
        ...prev,
        [field]: validateCloneReplacement(field, data, cloneProtectedFieldModes),
      }))
    }
  }

  const handleCloneProtectedFieldModeChange = (
    field: CloneField,
    value: CloneProtectedFieldModes[CloneField],
  ) => {
    setCloneProtectedFieldModes((previous) => ({ ...previous, [field]: value }))
    setErrors((previous) => ({ ...previous, [field]: undefined }))
    setTouched((previous) => ({ ...previous, [field]: false }))
  }

  const handleSubmit = async () => {
    const deviceIdErr = validate("deviceId", data.deviceId)
    const clientIdErr = validate("clientId", data.clientId)
    const sotiDeviceIdErr = validate("sotiDeviceId", data.sotiDeviceId)
    const collectionErrors = validateCollectionSecrets(
      data,
      isClone ? cloneProtectedFieldModes : undefined,
    )
    const cloneErrors: FormErrors = isClone
      ? {
          configurationPassword: validateCloneReplacement(
            "configurationPassword",
            data,
            cloneProtectedFieldModes,
          ),
          ticketmasterConsumerKey: validateCloneReplacement(
            "ticketmasterConsumerKey",
            data,
            cloneProtectedFieldModes,
          ),
        }
      : {}
    setErrors({
      deviceId: deviceIdErr,
      clientId: clientIdErr,
      sotiDeviceId: sotiDeviceIdErr,
      ...collectionErrors,
      ...cloneErrors,
    })
    setTouched({
      deviceId: true,
      clientId: true,
      sotiDeviceId: true,
      appleVendors: true,
      googleVendors: true,
      proVenueInstances: true,
      configurationPassword: isClone,
      ticketmasterConsumerKey: isClone,
    })
    if (
      deviceIdErr ||
      clientIdErr ||
      sotiDeviceIdErr ||
      collectionErrors.appleVendors ||
      collectionErrors.googleVendors ||
      collectionErrors.proVenueInstances ||
      cloneErrors.configurationPassword ||
      cloneErrors.ticketmasterConsumerKey
    ) return
    if (initialData != null && cloneSourceDevicePk != null) {
      await onCloneSubmit(
        cloneSourceDevicePk,
        formDataToClone(data, clients, cloneProtectedFieldModes),
      )
      return
    }
    await onSubmit(formDataToCreate(data, clients))
  }

  const canSubmit =
    !validate("deviceId", data.deviceId) &&
    !validate("clientId", data.clientId) &&
    !validate("sotiDeviceId", data.sotiDeviceId)

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:w-[540px] sm:max-w-[540px] bg-card border-l border-border p-0 flex flex-col [&>button]:hidden"
      >
        <SheetHeader className="px-6 py-5 border-b border-border">
          <div className="flex items-start justify-between">
            <div>
              <SheetTitle className="text-lg font-semibold text-foreground">
                {isClone ? "Clone Device" : "Add New Device"}
              </SheetTitle>
              <SheetDescription className="text-muted-foreground mt-1">
                {isClone
                  ? "Pre-filled from an existing device. Enter a unique Device ID; all other values can be edited."
                  : "Register a new Alfred device. Optional fields inherit from global defaults."}
              </SheetDescription>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onOpenChange(false)}
              className="text-muted-foreground hover:text-foreground hover:bg-secondary -mr-2 -mt-1"
            >
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </Button>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {isClone ? (
            <Alert className="mb-5">
              <Info />
              <AlertTitle>Protected values stay hidden</AlertTitle>
              <AlertDescription>
                By default, the backend securely copies the source device&apos;s ProVenue, Apple,
                and Google vendor credentials, Ticketmaster consumer key, and configuration
                password without displaying them. Choose a replacement or global default for
                any field below.
              </AlertDescription>
            </Alert>
          ) : null}
          <AlfredDeviceFormFields
            data={data}
            onChange={handleChange}
            errors={errors}
            touched={touched}
            onBlur={handleBlur}
            mode="add"
            isClone={isClone}
            cloneProtectedFieldModes={cloneProtectedFieldModes}
            onCloneProtectedFieldModeChange={handleCloneProtectedFieldModeChange}
            clients={clients}
            disabled={isSubmitting}
          />
        </div>

        <SheetFooter className="px-6 py-4 border-t border-border flex-row justify-between gap-3">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
            className="bg-card border-input text-muted-foreground hover:bg-secondary hover:text-foreground"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !canSubmit}
            className="bg-primary hover:bg-primary/90 text-primary-foreground disabled:opacity-50"
          >
            {isSubmitting && <LoadingSpinner size="sm" className="mr-2" />}
            {isSubmitting
              ? isClone ? "Cloning..." : "Creating..."
              : isClone ? "Clone Device" : "Create Device"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
